import React, { useEffect, useState, useCallback } from 'react';
import { auditAPI } from '../../api/client';
import { Search, Download } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

// Catálogo humano de acciones (#po56ffPn). Ordenado alfabéticamente al
// momento de armar el <option> list.
const ACTION_LABELS = {
  approve_qr_registration: 'Aprobar registro QR',
  archive_authority: 'Archivar autoridad',
  archive_client: 'Archivar cliente',
  archive_emergency_service: 'Archivar emergencia',
  archive_event: 'Archivar evento',
  archive_evaluation: 'Archivar evaluación',
  archive_location: 'Archivar locación',
  archive_project: 'Archivar proyecto',
  archive_template: 'Archivar plantilla',
  archive_templates: 'Archivar plantilla',
  archive_user: 'Archivar usuario',
  activate_template: 'Activar plantilla',
  assign_leader: 'Asignar líder',
  assign_levantador: 'Asignar levantador',
  bulk_distribute: 'Distribución masiva',
  bulk_equipment_assign: 'Asignar equipo masivo',
  create_authority: 'Crear autoridad',
  create_client: 'Crear cliente',
  create_emergency_service: 'Crear emergencia',
  create_evaluation: 'Crear evaluación',
  create_event: 'Crear evento',
  create_location: 'Crear locación',
  create_project: 'Crear proyecto',
  create_staff: 'Crear personal',
  create_template: 'Crear plantilla',
  create_user: 'Crear usuario',
  deliver_equipment: 'Entregar equipo',
  delete_authority: 'Eliminar autoridad',
  delete_client: 'Eliminar cliente',
  delete_emergency_service: 'Eliminar emergencia',
  delete_event: 'Eliminar evento',
  delete_evaluation: 'Eliminar evaluación',
  delete_location: 'Eliminar locación',
  delete_project: 'Eliminar proyecto',
  delete_staff: 'Eliminar personal',
  delete_template: 'Eliminar plantilla',
  delete_user: 'Eliminar usuario',
  force_publish_evaluation: 'Publicación forzada',
  impersonate: 'Modo espejo',
  link_authority_to_event: 'Vincular autoridad a evento',
  link_authority_to_location: 'Vincular autoridad a locación',
  login: 'Inicio de sesión',
  logout: 'Cierre de sesión',
  // #Fase0-B3 — acciones nuevas del ciclo de vida de plantillas (C6).
  new_template_version: 'Nueva versión de plantilla',
  publish_evaluation: 'Publicar evaluación',
  register_payment: 'Registrar pago',
  reject_qr_registration: 'Rechazar registro QR',
  reset_password: 'Restablecer contraseña',
  retire_template: 'Retirar plantilla',
  return_evaluation: 'Regresar evaluación',
  submit_evaluation: 'Enviar a revisión',
  submit_notification_sent: 'Notificación enviada',
  unarchive_client: 'Restaurar cliente',
  unarchive_event: 'Restaurar evento',
  unarchive_location: 'Restaurar locación',
  unarchive_project: 'Restaurar proyecto',
  unarchive_template: 'Restaurar plantilla',
  unarchive_templates: 'Restaurar plantilla',
  unlink_authority_from_event: 'Desvincular autoridad de evento',
  unlink_authority_from_location: 'Desvincular autoridad de locación',
  update_client: 'Actualizar cliente',
  update_evaluation: 'Actualizar evaluación',
  update_evaluation_metadata: 'Renombrar evaluación',
  update_event: 'Actualizar evento',
  update_items: 'Actualizar respuestas',
  update_location: 'Actualizar locación',
  update_project: 'Actualizar proyecto',
  update_staff: 'Actualizar personal',
  update_template: 'Actualizar plantilla',
  update_tour_config: 'Actualizar configuración de tour',
  update_user: 'Actualizar usuario',
  upload_evidence: 'Subir evidencia',
};

// #Fl0i705u — traducción de Entidad → Módulo en español.
const ENTITY_LABELS = {
  authority: 'Autoridad',
  authorities: 'Autoridad',
  client: 'Cliente',
  clients: 'Cliente',
  emergency_service: 'Emergencia',
  emergencies: 'Emergencia',
  evaluation: 'Evaluación',
  evaluations: 'Evaluación',
  eval_item: 'Respuesta de evaluación',
  event: 'Evento',
  events: 'Evento',
  event_staff: 'Personal del evento',
  location: 'Locación',
  locations: 'Locación',
  map_point: 'Punto en mapa',
  payment: 'Pago',
  project: 'Proyecto',
  projects: 'Proyecto',
  qr_registration: 'Registro QR',
  staff: 'Personal',
  staff_members: 'Personal',
  template: 'Plantilla',
  templates: 'Plantilla',
  user: 'Usuario',
  users: 'Usuario',
};

// #52TefQpP — traducir metadatos a lenguaje humano.
function humanizeChanges(value) {
  if (value == null) return '—';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value.map(v => typeof v === 'object' ? JSON.stringify(v) : String(v)).join(', ');
  }
  if (typeof value !== 'object') return String(value);
  const FIELD_LABELS = {
    name: 'Nombre',
    title: 'Título',
    status: 'Estado',
    role: 'Rol',
    email: 'Correo',
    phone: 'Teléfono',
    project_id: 'Proyecto',
    project: 'Proyecto',
    client_id: 'Cliente',
    leader_id: 'Líder',
    assigned_levantador_id: 'Levantador',
    template_id: 'Plantilla',
    template: 'Plantilla',
    version: 'Versión',
    location_id: 'Locación',
    event_id: 'Evento',
    is_active: 'Activo',
    is_archived: 'Archivado',
    is_blocking: 'Bloqueante',
    score: 'Puntaje',
    percentage: 'Porcentaje',
    semaforo: 'Semáforo',
    comments: 'Comentarios',
    notes: 'Notas',
    quantity_assigned: 'Cantidad asignada',
    quantity_returned: 'Cantidad devuelta',
    condition_on_delivery: 'Condición al entregar',
    condition_on_return: 'Condición al devolver',
    target_user: 'Usuario objetivo',
    target_role: 'Rol objetivo',
    filename: 'Archivo',
    url: 'URL',
  };
  const parts = [];
  for (const [k, v] of Object.entries(value)) {
    const label = FIELD_LABELS[k] || k;
    let val = v;
    if (val == null) val = '—';
    else if (typeof val === 'boolean') val = val ? 'Sí' : 'No';
    else if (typeof val === 'object') val = JSON.stringify(val);
    else val = String(val);
    if (val.length > 60) val = val.slice(0, 57) + '…';
    parts.push(`${label}: ${val}`);
  }
  return parts.join(' · ');
}

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    action: '',
    dateFrom: '',
    dateTo: '',
    limit: 50,
    offset: 0,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Backend acepta `skip`, no `offset` — usar el nombre correcto fixea
      // la paginación que siempre devolvía los mismos registros (#KklO0TkF).
      const params = { limit: filters.limit, skip: filters.offset };
      if (filters.action) params.action = filters.action;
      const r = await auditAPI.list(params);
      setLogs(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Error al cargar log de auditoría'); }
    finally { setLoading(false); }
  }, [filters.limit, filters.offset, filters.action]);

  useEffect(() => { load(); }, [load]);

  // Filtros client-side (search libre + rango de fechas — #BMJV4bga).
  const filtered = logs.filter((l) => {
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const matchesText = (
        l.user_email?.toLowerCase().includes(q) ||
        l.action?.toLowerCase().includes(q) ||
        (ACTION_LABELS[l.action] || '').toLowerCase().includes(q) ||
        l.entity?.toLowerCase().includes(q) ||
        (ENTITY_LABELS[l.entity] || '').toLowerCase().includes(q)
      );
      if (!matchesText) return false;
    }
    if (filters.dateFrom) {
      const ts = new Date(l.timestamp);
      const from = new Date(filters.dateFrom + 'T00:00:00');
      if (ts < from) return false;
    }
    if (filters.dateTo) {
      const ts = new Date(l.timestamp);
      const to = new Date(filters.dateTo + 'T23:59:59');
      if (ts > to) return false;
    }
    return true;
  });

  // #MbNWbscw — el PDF anterior usaba `filtered.slice(0, 100)` que solo
  // veía la página actual (50 items). Ahora trae TODOS los registros en
  // bloques antes de exportar.
  const exportPDF = async () => {
    setLoading(true);
    try {
      const BATCH = 200;
      let all = [];
      let skip = 0;
      while (true) {
        const params = { limit: BATCH, skip };
        if (filters.action) params.action = filters.action;
        const r = await auditAPI.list(params);
        const items = r.data.items || [];
        all = all.concat(items);
        if (items.length < BATCH) break;
        skip += BATCH;
        if (skip > 5000) break; // safeguard
      }
      // Aplica los mismos filtros client-side al export.
      const rows = all.filter((l) => {
        if (filters.search) {
          const q = filters.search.toLowerCase();
          if (!(
            l.user_email?.toLowerCase().includes(q) ||
            l.action?.toLowerCase().includes(q) ||
            (ACTION_LABELS[l.action] || '').toLowerCase().includes(q) ||
            l.entity?.toLowerCase().includes(q) ||
            (ENTITY_LABELS[l.entity] || '').toLowerCase().includes(q)
          )) return false;
        }
        if (filters.dateFrom && new Date(l.timestamp) < new Date(filters.dateFrom + 'T00:00:00')) return false;
        if (filters.dateTo && new Date(l.timestamp) > new Date(filters.dateTo + 'T23:59:59')) return false;
        return true;
      });

      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text('Log de Auditoría - SOEM', 14, 20);
      doc.setFontSize(10);
      doc.text(`Exportado: ${new Date().toLocaleString('es-MX')} · ${rows.length} registros`, 14, 28);
      let y = 38;
      doc.setFontSize(8);
      rows.forEach((l) => {
        if (y > 270) { doc.addPage(); y = 20; }
        const date = new Date(l.timestamp).toLocaleString('es-MX');
        const action = ACTION_LABELS[l.action] || l.action;
        const entity = ENTITY_LABELS[l.entity] || l.entity;
        doc.text(`${date} | ${l.user_email || '—'} | ${action} | ${entity}`, 14, y);
        y += 6;
      });
      doc.save('auditoria-soem.pdf');
      toast.success(`PDF exportado (${rows.length} registros)`);
    } catch {
      toast.error('Error al exportar PDF');
    } finally {
      setLoading(false);
    }
  };

  const uniqueActions = [...new Set(logs.map((l) => l.action))]
    .sort((a, b) => (ACTION_LABELS[a] || a).localeCompare(ACTION_LABELS[b] || b, 'es'));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-log-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">Log de Auditoría</h1>
          <p className="text-slate-500 text-sm mt-1">{total} registros en el sistema</p>
        </div>
        <button onClick={exportPDF} className="soem-btn-secondary px-4 py-2 text-sm flex items-center gap-2" data-testid="export-pdf-btn">
          <Download size={16} /> Exportar PDF
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="pl-9 pr-4 py-2 border border-slate-300 rounded-sm text-sm w-64 focus:outline-none focus:ring-2 focus:ring-soem-primary"
            placeholder="Buscar por usuario, módulo…" aria-label="Buscar en bitácora"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            data-testid="audit-search-input"
          />
        </div>
        <select
          aria-label="Filtrar por acción"
          className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
          value={filters.action}
          onChange={(e) => setFilters({ ...filters, action: e.target.value, offset: 0 })}
          data-testid="audit-action-filter"
        >
          <option value="">Todas las acciones</option>
          {uniqueActions.map((a) => (
            <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
          ))}
        </select>
        {/* Rango de fechas (#BMJV4bga). */}
        <input
          type="date"
          aria-label="Desde"
          className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
          value={filters.dateFrom}
          onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
          data-testid="audit-date-from"
        />
        <input
          type="date"
          aria-label="Hasta"
          className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
          value={filters.dateTo}
          onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
          data-testid="audit-date-to"
        />
        {(filters.dateFrom || filters.dateTo || filters.search) && (
          <button
            onClick={() => setFilters({ ...filters, search: '', dateFrom: '', dateTo: '' })}
            className="soem-btn-secondary px-3 py-2 text-xs"
            data-testid="audit-clear-btn"
          >
            Limpiar
          </button>
        )}
      </div>

      <div className="soem-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="soem-table-header">
              <th className="px-4 py-3 text-left">Fecha/Hora</th>
              <th className="px-4 py-3 text-left">Usuario</th>
              <th className="px-4 py-3 text-left">Acción</th>
              {/* #Al0XU4O8 — renombrado: Entidad → Módulo, Detalles → Cambios. */}
              <th className="px-4 py-3 text-left">Módulo</th>
              <th className="px-4 py-3 text-left">Cambios</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(8)].map((_, i) => (
                <tr key={i}><td colSpan={5} className="px-4 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td></tr>
              ))
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="text-center text-slate-400 py-8">No se encontraron registros.</td></tr>
            ) : (
              filtered.map((l) => (
                <tr key={l.id} className="soem-table-row" data-testid={`audit-row-${l.id}`}>
                  <td className="px-4 py-3 text-slate-500 text-xs font-mono whitespace-nowrap">
                    {new Date(l.timestamp).toLocaleString('es-MX')}
                  </td>
                  <td className="px-4 py-3 text-soem-navy font-medium">{l.user_email}</td>
                  <td className="px-4 py-3">
                    <span className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">
                      {ACTION_LABELS[l.action] || l.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{ENTITY_LABELS[l.entity] || l.entity}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs max-w-md">
                    <span className="block truncate" title={humanizeChanges(l.new_value)}>
                      {humanizeChanges(l.new_value)}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>
          Mostrando {filtered.length} de {total}
          {(filters.dateFrom || filters.dateTo || filters.search) && filtered.length !== logs.length && (
            <span className="text-slate-400"> (filtrados de {logs.length} en página)</span>
          )}
        </span>
        <div className="flex gap-2">
          <button
            disabled={filters.offset === 0 || loading}
            onClick={() => setFilters({ ...filters, offset: Math.max(0, filters.offset - filters.limit) })}
            className="soem-btn-secondary px-3 py-1.5 text-xs disabled:opacity-40"
            data-testid="audit-prev-btn"
          >
            Anterior
          </button>
          <button
            disabled={filters.offset + filters.limit >= total || loading}
            onClick={() => setFilters({ ...filters, offset: filters.offset + filters.limit })}
            className="soem-btn-secondary px-3 py-1.5 text-xs disabled:opacity-40"
            data-testid="audit-next-btn"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  );
}
